import { useState, useEffect, useCallback } from 'react';

// shadcn UI components
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Loader } from '../../components/ui/loader';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider
} from '../../components/ui/tooltip';

// icons
import { SearchNormal, TickSquare, Refresh } from 'iconsax-react';

// services
import roleService from '../../services/roleService';

// components
import PermissionCheckbox from '../../components/roles/PermissionCheckbox';

// utils
import { getPermissionErrorMessage } from '../../utils/permissionMessages';
import { cn } from '../../lib/utils';

const PermissionMatrix = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [showSystemRoles, setShowSystemRoles] = useState(true);
  const [showCustomRoles, setShowCustomRoles] = useState(true);

  // Group permissions by category (matching backend permission names)
  const permissionCategories = {
    'User Management': ['user.create', 'user.read', 'user.update', 'user.delete'],
    'Role Management': ['role.create', 'role.read', 'role.update', 'role.delete', 'role.assign'],
    'Client Management': ['client.create', 'client.read', 'client.update', 'client.delete', 'client.suspend'],
    'Patient Management': ['patient.create', 'patient.read', 'patient.update', 'patient.delete', 'patient.medical'],
    'Caregiver Management': ['caregiver.create', 'caregiver.read', 'caregiver.update', 'caregiver.delete', 'caregiver.assign'],
    'Care Plans': ['careplan.create', 'careplan.read', 'careplan.update', 'careplan.delete'],
    'Financial': ['billing.read', 'billing.manage', 'financial.reports'],
    'System': ['system.config', 'system.maintenance'],
    'Audit & Compliance': ['audit.read', 'audit.export', 'compliance.reports']
  };

  const fetchMatrixData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [rolesResult, matrixResult] = await Promise.all([
        roleService.getAllRoles({ limit: 100 }), // Get all roles
        roleService.getPermissionMatrix()
      ]);

      if (rolesResult.success && matrixResult.success) {
        const rolesData = rolesResult.data.roles || [];
        const matrixData = matrixResult.data;
        
        setRoles(rolesData);
        setPermissions(matrixData.permissions || []);
        
        // Build matrix object for easy lookup
        const matrixObj = {};
        rolesData.forEach(role => {
          matrixObj[role._id] = role.permissions || [];
        });
        setMatrix(matrixObj);
      } else {
        setError('Failed to load permission matrix data');
      }
    } catch (error) {
      console.error('Error fetching matrix data:', error);
      if (error.response?.data?.error?.code === 'INSUFFICIENT_PERMISSIONS') {
        const friendlyMessage = getPermissionErrorMessage(error.response.data.error.message);
        setError(friendlyMessage);
      } else {
        setError('Failed to load permission matrix');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMatrixData();
  }, [fetchMatrixData]);

  // Set default selected role on load
  useEffect(() => {
    if (roles.length > 0 && !selectedRoleId) {
      setSelectedRoleId(roles[0]._id);
    }
  }, [roles, selectedRoleId]);

  const handlePermissionToggle = (roleId, permission) => {
    setMatrix(prev => {
      const rolePermissions = prev[roleId] || [];
      const hasPermission = rolePermissions.includes(permission);
      
      return {
        ...prev,
        [roleId]: hasPermission
          ? rolePermissions.filter(p => p !== permission)
          : [...rolePermissions, permission]
      };
    });
  };

  const handleBulkPermissionToggle = (permission, enable) => {
    const filteredRoles = getFilteredRoles();
    setMatrix(prev => {
      const newMatrix = { ...prev };
      filteredRoles.forEach(role => {
        if (!role.isSystem) { // Don't modify system roles
          const rolePermissions = newMatrix[role._id] || [];
          if (enable && !rolePermissions.includes(permission)) {
            newMatrix[role._id] = [...rolePermissions, permission];
          } else if (!enable && rolePermissions.includes(permission)) {
            newMatrix[role._id] = rolePermissions.filter(p => p !== permission);
          }
        }
      });
      return newMatrix;
    });
  };

  const handleRoleBulkToggle = (roleId, permissionsList, enable) => {
    const role = roles.find(r => r._id === roleId);
    if (role?.isSystem) return; // Don't modify system roles

    setMatrix(prev => {
      const rolePermissions = prev[roleId] || [];
      if (enable) {
        const newPermissions = [...new Set([...rolePermissions, ...permissionsList])];
        return { ...prev, [roleId]: newPermissions };
      } else {
        const filteredPermissions = rolePermissions.filter(p => !permissionsList.includes(p));
        return { ...prev, [roleId]: filteredPermissions };
      }
    });
  };

  const handleCategoryBulkToggle = (categoryPermissions, enable) => {
    const filteredRoles = getFilteredRoles();
    setMatrix(prev => {
      const newMatrix = { ...prev };
      filteredRoles.forEach(role => {
        if (!role.isSystem) { // Don't modify system roles
          const rolePermissions = newMatrix[role._id] || [];
          if (enable) {
            const newPermissions = [...new Set([...rolePermissions, ...categoryPermissions])];
            newMatrix[role._id] = newPermissions;
          } else {
            const filteredPermissions = rolePermissions.filter(p => !categoryPermissions.includes(p));
            newMatrix[role._id] = filteredPermissions;
          }
        }
      });
      return newMatrix;
    });
  };

  const saveChanges = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const updates = [];
      for (const [roleId, rolePermissions] of Object.entries(matrix)) {
        const role = roles.find(r => r._id === roleId);
        if (role && !role.isSystem) { // Only update custom roles
          updates.push(
            roleService.updateRole(roleId, { permissions: rolePermissions })
          );
        }
      }

      await Promise.all(updates);
      setSuccess('Permission matrix updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving changes:', error);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getFilteredRoles = () => {
    return roles.filter(role => {
      const matchesSearch = role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           role.displayName?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = (showSystemRoles && role.isSystem) || (showCustomRoles && !role.isSystem);
      return matchesSearch && matchesType;
    });
  };

  const getPermissionCount = (roleId) => {
    return matrix[roleId]?.length || 0;
  };

  if (loading) {
    return <Loader className="h-[calc(100vh-200px)] w-full" />;
  }

  const filteredRoles = getFilteredRoles();
  const selectedRole = roles.find(r => r._id === selectedRoleId) || filteredRoles[0];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          Permission Matrix
        </h1>
        <p className="text-sm text-muted-foreground">
          Visual interface for managing role permissions across the system
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-emerald-50 text-emerald-700 border-emerald-200">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Controls Card */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="w-full md:w-1/3 relative">
              <SearchNormal className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                placeholder="Search roles or permissions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-full bg-background"
              />
            </div>
            
            <div className="flex flex-wrap items-center gap-6 w-full md:w-auto">
              <div className="flex items-center space-x-2">
                <Switch
                  id="systemRoles"
                  checked={showSystemRoles}
                  onCheckedChange={setShowSystemRoles}
                />
                <Label htmlFor="systemRoles" className="text-sm font-medium text-foreground cursor-pointer select-none">
                  System Roles
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="customRoles"
                  checked={showCustomRoles}
                  onCheckedChange={setShowCustomRoles}
                />
                <Label htmlFor="customRoles" className="text-sm font-medium text-foreground cursor-pointer select-none">
                  Custom Roles
                </Label>
              </div>

              <div className="ml-auto flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={fetchMatrixData}
                  disabled={loading}
                  className="flex items-center gap-1.5"
                >
                  <Refresh size={18} />
                  Refresh
                </Button>
                <Button
                  onClick={saveChanges}
                  disabled={saving}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1.5"
                >
                  <TickSquare size={18} />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Two-Column Side-by-Side Canvas */}
      <div className="grid grid-cols-12 gap-6">
        {/* Roles Sidebar (Left, 3 Cols) */}
        <div className="col-span-12 md:col-span-3">
          <Card className="h-full">
            <CardContent className="p-4 space-y-4">
              <div className="border-b pb-2">
                <h3 className="text-base font-semibold text-foreground">
                  Roles Selection
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Select a role to configure permissions
                </p>
              </div>
              
              <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                {filteredRoles.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-6">
                    No roles matched filters
                  </p>
                ) : (
                  filteredRoles.map((role) => {
                    const isSelected = selectedRoleId === role._id;
                    const count = getPermissionCount(role._id);
                    return (
                      <div
                        key={role._id}
                        onClick={() => setSelectedRoleId(role._id)}
                        className={cn(
                          "p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 flex items-center justify-between gap-2.5",
                          isSelected
                            ? "border-primary bg-primary/5 font-medium"
                            : "border-slate-100 hover:border-slate-350 hover:bg-slate-50/50"
                        )}
                      >
                        <div className="overflow-hidden">
                          <p className={cn("text-xs font-semibold truncate", isSelected ? "text-primary" : "text-foreground")}>
                            {role.displayName || role.name}
                          </p>
                          <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{role.name}</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Badge className={cn("text-[9px] font-semibold border-none px-1.5 py-0.25", 
                            role.isSystem ? "bg-primary/10 text-primary hover:bg-primary/10" : "bg-slate-100 text-slate-700 hover:bg-slate-100"
                          )}>
                            {role.isSystem ? 'System' : 'Custom'}
                          </Badge>
                          <span className="text-[10px] font-bold text-muted-foreground bg-slate-100 px-1.5 py-0.5 rounded-full shrink-0">
                            {count}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Permissions Grid (Right, 9 Cols) */}
        <div className="col-span-12 md:col-span-9">
          {selectedRole ? (
            <Card>
              <CardContent className="p-6 space-y-6">
                {/* Header info */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b pb-4 gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-foreground">
                      {selectedRole.displayName || selectedRole.name} Permissions
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">
                      {selectedRole.description || "No description provided."}
                    </p>
                  </div>

                  {!selectedRole.isSystem && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRoleBulkToggle(selectedRole._id, permissions, true)}
                        className="text-xs font-semibold h-8"
                      >
                        Grant All
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRoleBulkToggle(selectedRole._id, permissions, false)}
                        className="text-xs font-semibold h-8 text-destructive hover:text-destructive hover:bg-destructive/5 border-destructive/20 hover:border-destructive/30"
                      >
                        Revoke All
                      </Button>
                    </div>
                  )}
                </div>

                {selectedRole.isSystem && (
                  <Alert className="bg-blue-50 text-blue-800 border-blue-200">
                    <AlertDescription className="text-xs font-medium">
                      System Role: Permissions are read-only and cannot be updated.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Categorized list */}
                <div className="space-y-6">
                  {Object.entries(permissionCategories).map(([category, categoryPermissions]) => {
                    const visiblePermissions = categoryPermissions.filter(p => 
                      permissions.includes(p) && 
                      p.toLowerCase().includes(searchTerm.toLowerCase())
                    );

                    if (visiblePermissions.length === 0) return null;

                    const rolePermissions = matrix[selectedRole._id] || [];
                    const activeInCategory = visiblePermissions.filter(p => rolePermissions.includes(p));
                    const allActive = activeInCategory.length === visiblePermissions.length;

                    return (
                      <Card key={category} className="border border-slate-150 shadow-none">
                        <CardContent className="p-4 space-y-4">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b pb-2 gap-2">
                            <h3 className="text-sm font-semibold text-foreground">
                              {category} ({activeInCategory.length}/{visiblePermissions.length})
                            </h3>
                            {!selectedRole.isSystem && (
                              <div className="flex flex-wrap items-center gap-1.5">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleRoleBulkToggle(selectedRole._id, visiblePermissions, !allActive)}
                                  className="text-[11px] font-semibold h-7 px-2"
                                >
                                  {allActive ? "Revoke Category" : "Grant Category"}
                                </Button>
                                <div className="h-4 w-[1px] bg-slate-200 mx-1 hidden sm:block"></div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleCategoryBulkToggle(visiblePermissions, true)}
                                  className="text-[11px] font-semibold text-primary h-7 px-2 hover:bg-primary/5"
                                >
                                  Grant Category to All Custom Roles
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleCategoryBulkToggle(visiblePermissions, false)}
                                  className="text-[11px] font-semibold text-destructive h-7 px-2 hover:bg-destructive/5"
                                >
                                  Revoke Category from All Custom Roles
                                </Button>
                              </div>
                            )}
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {visiblePermissions.map((permission) => {
                              const isChecked = rolePermissions.includes(permission);
                              return (
                                <div key={permission} className="flex items-start space-x-2.5 p-2 rounded-md hover:bg-slate-50 transition-colors">
                                  <PermissionCheckbox
                                    checked={isChecked}
                                    onChange={() => handlePermissionToggle(selectedRole._id, permission)}
                                    disabled={selectedRole.isSystem}
                                    permission={permission}
                                    roleName={selectedRole.displayName || selectedRole.name}
                                  />
                                  <div className="space-y-0.5 flex-1 overflow-hidden">
                                    <div className="flex items-center justify-between gap-2 overflow-hidden">
                                      <Label 
                                        htmlFor={`matrix-perm-${permission}`}
                                        className="text-xs font-semibold text-foreground cursor-pointer block select-none leading-tight truncate"
                                      >
                                        {permission.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                                      </Label>
                                      
                                      {!selectedRole.isSystem && (
                                        <TooltipProvider>
                                          <div className="flex items-center gap-1 shrink-0">
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <button 
                                                  type="button"
                                                  onClick={(e) => { e.stopPropagation(); handleBulkPermissionToggle(permission, true); }}
                                                  className="text-[9px] px-1 py-0.25 text-emerald-600 hover:bg-emerald-50 rounded border border-emerald-100 font-medium"
                                                >
                                                  Grant All
                                                </button>
                                              </TooltipTrigger>
                                              <TooltipContent>Grant this permission to all Custom Roles</TooltipContent>
                                            </Tooltip>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <button 
                                                  type="button"
                                                  onClick={(e) => { e.stopPropagation(); handleBulkPermissionToggle(permission, false); }}
                                                  className="text-[9px] px-1 py-0.25 text-rose-600 hover:bg-rose-50 rounded border border-rose-100 font-medium"
                                                >
                                                  Revoke All
                                                </button>
                                              </TooltipTrigger>
                                              <TooltipContent>Revoke this permission from all Custom Roles</TooltipContent>
                                            </Tooltip>
                                          </div>
                                        </TooltipProvider>
                                      )}
                                    </div>
                                    <span className="text-[10px] text-muted-foreground font-mono block select-none truncate">
                                      {permission}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-20 text-sm text-muted-foreground">
              Select a role in the left panel to begin configuring permissions.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionMatrix;